import * as React from 'react';
import { Form } from 'react-bootstrap';
import {
  AgencyDownlineRepository,
  IndNoteRepository,
  IndRepository,
  StaffRepository,
  UserActivityCategoryRepository,
  UserActivityRepository,
  UserAlertRepository,
} from '../../MedicareProAPI';
import type { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import type { UserActivity } from '../../MedicareProAPI/Entities/User/UserActivity';
import StyledModal from '../Components/StyledModal';
import { IndSearch } from '../Components/Form/Field/IndSearch';
import type { Staff } from '../../MedicareProAPI/Entities/Staff/Staff';
import type { AgencyDownline } from '../../MedicareProAPI/Entities/Agency/AgencyDownline';

const STATUS_OPTIONS = [
  'Working on it',
  'Waiting on Reply',
  'Stuck',
  'Paused',
  'Completed',
  'Canceled',
];

const PRIORITY_OPTIONS = ['Low', 'Med', 'High'];

type AssignableOption = {
  value: string | number;
  label: string;
};

interface ActivityModalProps {
  userAccessToken: string;
  userId: number;
  agencyId?: number;
  directUplineId?: number;
  absoluteUplineId?: number;
  staffId?: number;
  indId?: number;
  show: boolean;
  activity?: UserActivity;
  onClose?: () => void;
  onCreate?: (payload: any) => Promise<void>;
  customCategories?: string[];
  assignableOptions?: AssignableOption[];
}

/**
 * Activity Modal (self or staff/downline)
 *
 * @component
 * @author Andrew Brown
 * @date Created: 2025-12-04
 * @version 1.1.1
 */
export const ActivityModal = ({
  userAccessToken,
  userId,
  agencyId,
  directUplineId,
  absoluteUplineId,
  staffId,
  indId,
  show,
  activity,
  onClose,
  onCreate,
  assignableOptions = [],
}: ActivityModalProps) => {
  // Repositories – memoize so we don't recreate on every render
  const userActivityRepository = React.useMemo(() => {
    const repo = new UserActivityRepository();
    repo.setAccessToken(userAccessToken);
    return repo;
  }, [userAccessToken]);

  const userActivityCategoryRepository = React.useMemo(() => {
    const repo = new UserActivityCategoryRepository();
    repo.setAccessToken(userAccessToken);
    return repo;
  }, [userAccessToken]);

  const staffRepository = React.useMemo(() => {
    const repo = new StaffRepository();
    repo.setAccessToken(userAccessToken);
    return repo;
  }, [userAccessToken]);

  const agencyDownlineRepository = React.useMemo(() => {
    const repo = new AgencyDownlineRepository();
    repo.setAccessToken(userAccessToken);
    return repo;
  }, [userAccessToken]);

  const indRepository = React.useMemo(() => {
    const repo = new IndRepository();
    repo.setAccessToken(userAccessToken);
    return repo;
  }, [userAccessToken]);

  const [status, setStatus] = React.useState<string>(STATUS_OPTIONS[0]);
  const [priority, setPriority] = React.useState<string>(PRIORITY_OPTIONS[0]);
  const [assignTo, setAssignTo] = React.useState<string | number>(
    assignableOptions[0]?.value ?? ''
  );
  const [category, setCategory] = React.useState<string>('');
  const [deadline, setDeadline] = React.useState<string>('');
  const [affiliationName, setAffiliationName] = React.useState<string>('');
  const [affiliationId, setAffiliationId] = React.useState<string>('');
  const [affiliation, setAffiliation] = React.useState<Ind | null>();
  const [title, setTitle] = React.useState<string>('');
  const [details, setDetails] = React.useState<string>('');
  const [duplicateForSpouse, setDuplicateForSpouse] = React.useState<boolean>(false);

  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const [categories, setCategories] = React.useState<Array<any>>([]);
  const [staff, setStaff] = React.useState<Array<Staff>>([]);
  const [downlines, setDownlines] = React.useState<Array<AgencyDownline>>([]);

  const hasAssignTo = staff.length > 0 || downlines.length > 0;
  // If agency id -> get LOAs and staff
  // If user is staff -> get LOAS and principal and staff

  // get custom categories

  const getCategories = () => {
    userActivityRepository.getUserActivitiesCategories(userId).then((response) => {
      setCategories(response);
    });
  };

  const getStaff = () => {
    staffRepository.getUserStaff(userId).then((response) => {
      setStaff(response.getItems());

      const meStaff = response.getItems().find((dwn) => dwn.getStaffId() === staffId);

      if (meStaff) {
        setAssignTo(meStaff.getStaffId());
      }
    });
  };

  const getDownlines = () => {
    agencyDownlineRepository
      .getAgencyLoasAndPrincipal(userId, agencyId || directUplineId)
      .then((response) => {
        setDownlines(response.getItems());

        const meDownline = response.getItems().find((dwn) => dwn.getUser().getUserId() === userId);

        if (meDownline) {
          setAssignTo(meDownline.getUser().getUserId());
        }
      });
  };

  const getInd = async (indId: number) => {
    const ind = await indRepository.getInd(indId);

    setAffiliation(ind);
  };

  React.useEffect(() => {
    if (!show) return;

    getCategories();
    getStaff();

    if (activity) {
      activity?.getActivityDetails() ? setDetails(activity?.getActivityDetails()) : '';
      activity?.getActivityTitle() ? setTitle(activity?.getActivityTitle()) : '';
    }

    if (agencyId || directUplineId) {
      getDownlines();
    }
    // void fetchCustomCategories();
    // void fetchStaffAndDownlines();
  }, [userId, userAccessToken, show]);

  // Get the full individual
  React.useEffect(() => {
    if (typeof indId !== 'number') return;

    getInd(indId);
  }, [indId]);

  const handleCategoryChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    if (value === 'addNewCategory') {
      const newName = window.prompt('Enter a new activity category name:')?.trim();

      if (!newName) {
        // Reset select back to previous value if user cancels/empties
        e.target.value = category;
        return;
      }

      try {
        const payload = {
          name: newName,
        };
        await userActivityCategoryRepository.addUserActivityCategory(userId, payload);
        // Update local list + select it
        setCategories((prev) => (prev.includes(newName) ? prev : [...prev, { name: newName }]));
        setCategory(newName);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Unable to create category. Please try again.';
        setError(msg);
        // reset select back to previous value
        e.target.value = category;
      }

      return;
    }

    setCategory(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    const trimmedDetails = details.trim();

    if (!trimmedTitle) {
      setError('Activity title is required.');
      return;
    }

    if (!category) {
      setError('Category is required.');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const payload = {
        activityStatus: status,
        activityPriority: priority,
        activityCategory: category,
        activityDeadline: deadline || null,
        activityAffiliation: affiliation ? affiliation.getIndId() : null,
        activityTitle: trimmedTitle,
        activityDetails: trimmedDetails,
        //activitySpouse: duplicateForSpouse ? 1 : 0,
        activityToId: assignTo ? assignTo : staffId || userId,
        activityFromId: staffId || userId,
      };
      await userActivityRepository.addUserActivity(userId, payload);
      if (onCreate) {
        await onCreate(payload);
      }

      onClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Unable to save activity. Please try again.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <StyledModal
      size="lg"
      title={'New Activity'}
      footerCloseButton={true}
      show={show}
      onClose={onClose}
    >
      {/* Body */}
      <form onSubmit={handleSubmit} id="newActivityForm">
        <div className="row gutter-2 fusion-ignore">
          {/* Status */}
          <div className="col-12 mb-2">
            <label className="label">Status</label>
            <select
              name="activity_status"
              id="act_status"
              className="form-control form-control-sm keep"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              required
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {/* Priority / AssignTo / Category */}
          <div className={`col-12 ${hasAssignTo ? 'col-md-4' : 'col-md-6'} mb-2`}>
            <label className="label">Priority</label>
            <select
              name="activity_priority"
              id="act_priority"
              className="form-control form-control-sm keep"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              required
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {(staff.length > 0 || downlines.length > 0) && (
            <div className="col-12 col-md-4 mb-2">
              <label className="label">Assign to</label>
              <select
                name="activity_to_id"
                className="form-control form-control-sm keep"
                value={assignTo}
                onChange={(e) => setAssignTo(e.target.value)}
                required
              >
                {staff.length > 0 &&
                  staff.map((stf, indx) => (
                    <option key={`staff-${stf.getStaffId()}`} value={stf.getStaffId()}>
                      {`${stf.getStaffFirstname()} ${stf.getStaffLastname()}`}
                    </option>
                  ))}

                {downlines.length > 0 &&
                  downlines.map((dwn, indx) => (
                    <option
                      key={`dl-${dwn.getUser().getUserId()}`}
                      value={dwn.getUser().getUserId()}
                    >
                      {`${dwn.getUser().getUserFirstname()} ${dwn.getUser().getUserLastname()}`}
                    </option>
                  ))}
              </select>
            </div>
          )}

          <div className={`col-12 ${hasAssignTo ? 'col-md-4' : 'col-md-6'} mb-2`}>
            <label className="label">Category</label>
            <select
              name="activity_category"
              id="act_category"
              className="form-control form-control-sm"
              value={category}
              onChange={handleCategoryChange}
              required
            >
              <option value="">Select..</option>
              {categories.map((cat, catIdx) => (
                <option key={cat.name + `-` + catIdx} value={cat.name}>
                  {cat.name}
                </option>
              ))}
              <option value="addNewCategory">ADD NEW</option>
            </select>
          </div>
        </div>

        {/* Deadline + Affiliation */}
        <div className="row gutter-2">
          <div className="col-12 col-md-6 mb-2">
            <label className="label">Deadline</label>
            <input
              type="date"
              className="form-control form-control-sm needsSlashes"
              name="activity_deadline"
              id="act_deadline"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 mb-2" id="activityAffilDropdown">
            <label className="label">
              Link to Profile
              <i
                className="fal fa-question-circle cp ml-2"
                data-toggle="popover"
                data-content="If you want to link this activity to a client, type their name and select from results."
              />
            </label>
            <IndSearch
              onChange={(individual) => setAffiliation(individual ? individual : null)}
              include={[]}
              value={affiliation ? affiliation : null}
            />
            {/* You can render dropdown results here */}
            <div
              className="dropdown-menu"
              aria-labelledby="affiliationSearch"
              style={{
                marginRight: 30,
                marginTop: 5,
                position: 'absolute',
                zIndex: 999,
                width: 400,
              }}
              id="affiliationSearchReturn"
            />
          </div>
        </div>

        {/* Title / Details / Spouse */}
        <div className="row">
          <div className="col-12 mb-2">
            <label className="label">Activity Title (20 character max)</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Call John Doe"
              name="activity_title"
              id="act_title"
              maxLength={20}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="col-12">
            <label className="label">Activity Details</label>
            <textarea
              className="form-control actDets"
              autoCorrect="off"
              name="activity_details"
              id="act_details"
              cols={30}
              rows={7}
              placeholder="John just moved out of the service area and qualifies for an SEP, call him to gather information and find a new plan."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
            />
          </div>
          <div className="col-12 mt-2 hiddenStuff activityForSpouseDiv">
            <label className="mb-0">
              <input
                type="checkbox"
                className="mr-1"
                checked={duplicateForSpouse}
                onChange={(e) => setDuplicateForSpouse(e.target.checked)}
                name="activity_spouse"
              />
              Duplicate for spouse?
            </label>
          </div>
        </div>

        {/* hidden fields equivalents */}
        <input type="hidden" value="activities" name="page" />
        <input
          type="hidden"
          name="activity_affiliation"
          id="act_affiliation"
          value={affiliationId}
        />

        {error && (
          <div className="mt-2 alert alert-danger py-1">
            <i className="far fa-exclamation-triangle mr-1" />
            {error}
          </div>
        )}

        <button type="submit" className="btn btn-primary btn-sm mt-3" disabled={saving}>
          {saving ? (
            <>
              <i className="far fa-spinner fa-spin mr-1" /> Saving...
            </>
          ) : (
            <>Save</>
          )}
        </button>
      </form>
    </StyledModal>
  );
};
